/**
 * Retail > Allocation. Weekly warehouse-to-store allocation runs.
 *
 * /retail/allocation — shows the most recent allocation run by default (a
 * runs dropdown lets the operator look at any prior run), the run's KPI
 * totals, and a category/sub-category summary of the run aggregated across
 * every store: SKU counts, last-week sales, trend, units allocated, extended
 * cost, and stockouts. The full store x item detail deliberately lives in the
 * Excel export only (2026-08-09 rework: the old ~6,900-row store x SKU grid
 * made the page slow, and Brett works from the export anyway).
 * Editors/admins (retail.allocation.run) can trigger a fresh run; viewers
 * (retail.allocation.read) get read-only access to runs, the summary, and
 * the export.
 *
 * A run can cover the whole allocation list or only chosen stores (ISD-362:
 * Brett runs the fleet one week and five stores the next). Whole fleet is the
 * default, so the ordinary weekly run is one click as before.
 */

import { useEffect, useState } from 'react';
import { Download, Loader2, PlayCircle, X } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { InfoTooltip, gridTheadClassName } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import {
  useAllocationRuns,
  useAllocationRunSummary,
  useAllocationStores,
  useRunAllocation,
  resolveRunStoreScope,
  downloadAllocationXlsx,
  hasAllocationRead,
  hasAllocationRun,
  formatUnits,
  formatAllocCost,
  trendLabel,
  TREND_TONES,
  type AllocationSummaryRow,
  type AllocationTrend,
} from '@/lib/useAllocation';
import { formatDateTime } from '@/lib/dateUtils';
import { AllocationInsights } from './AllocationInsights';

/**
 * Plain-English column help, rendered as InfoTooltips on the summary headers.
 * Written for operators who did not build the allocation file (the terms are
 * Brett's supply-chain vocabulary).
 */
const COLUMN_HELP: Record<string, string> = {
  Category:
    "Leadership's product category (Bulk, Novelty, Gourmet, L&P Collection, International, ...), as tagged on the SKU in CounterPoint. Bulk quantities are in pounds.",
  'Sub-category': "Leadership's sub-category within the category (for example Bulk Gummy inside Bulk).",
  SKUs: 'Active SKUs in this category across all stores: items with stock on hand, sales in the last four weeks, or units allocated. The smaller number after the slash is SKUs in the allocation config with no activity this run.',
  'Last wk': 'Units every store together sold in this category last week.',
  Avg: 'Weighted average weekly sales for the category across all stores. Recent weeks count more: last week 40%, then 30%, 20%, 10%.',
  Trend: 'Last week versus the average, for the whole category. Up = 25%+ above average, Down = 25%+ below, No sales = nothing sold in 4 weeks.',
  Suggested: 'Units the engine wants to send across all stores, before the warehouse-supply check.',
  Final: 'Units actually allocated after the warehouse check (proportional shares when supply is short).',
  'Ext cost': 'Final units times unit cost, summed across the category.',
  Stockouts: 'Store-SKU pairs in this category that normally sell but have zero (or negative) on-hand right now.',
};

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="text-lg font-bold leading-tight text-amp-ink dark:text-amp-dark-ink">{value}</div>
      <div className="mt-0.5 text-[11px] leading-tight text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</div>
    </div>
  );
}

function TrendPill({ trend }: { trend: AllocationTrend }) {
  return <Pill tone={TREND_TONES[trend]}>{trendLabel(trend)}</Pill>;
}

function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        Allocation is gated by the retail.allocation.read capability.
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Ask IT to grant you access.</p>
    </div>
  );
}

function NoRunsCard({ canRun, onRun, busy }: { canRun: boolean; onRun: () => void; busy: boolean }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">No allocation runs yet.</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {canRun
          ? 'Run the allocation to generate the first store-by-item recommendation.'
          : 'Ask a retail editor or admin to run the first allocation.'}
      </p>
      {canRun && (
        <button
          type="button"
          onClick={onRun}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-1 rounded border border-amp-ink bg-amp-ink px-3 py-1.5 text-xs text-amp-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
          Run allocation
        </button>
      )}
    </div>
  );
}

function ConfirmRunModal({
  onConfirm,
  onClose,
  busy,
}: {
  onConfirm: (storeNos: string[]) => void;
  onClose: () => void;
  busy: boolean;
}) {
  // Which stores this run covers. Brett runs the whole fleet most weeks and a
  // handful of stores in others (ISD-362), so the whole fleet stays the default
  // and narrowing it is a deliberate act.
  const { stores, isLoading, error } = useAllocationStores();
  const [wholeFleet, setWholeFleet] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  function toggle(storeNo: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(storeNo)) next.delete(storeNo);
      else next.add(storeNo);
      return next;
    });
  }

  const scope = resolveRunStoreScope({
    wholeFleet,
    picked: [...picked],
    eligibleCount: stores.length,
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative flex max-h-[85vh] w-full max-w-md flex-col rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Run allocation</h2>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          This computes a fresh store-by-item allocation for the most recent complete week using
          current warehouse on-hand and sales history, then saves it as a new run. It does not push
          anything to CounterPoint by itself; export the result afterward.
        </p>

        <fieldset className="mb-4 flex min-h-0 flex-col">
          <legend className="mb-2 text-xs font-medium uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Stores
          </legend>

          <label className="flex items-center gap-2 text-sm text-amp-ink dark:text-amp-dark-ink">
            <input
              type="radio"
              name="alloc-store-scope"
              checked={wholeFleet}
              onChange={() => setWholeFleet(true)}
              disabled={busy}
            />
            Every store on the allocation list
          </label>
          <label className="mt-1 flex items-center gap-2 text-sm text-amp-ink dark:text-amp-dark-ink">
            <input
              type="radio"
              name="alloc-store-scope"
              checked={!wholeFleet}
              onChange={() => setWholeFleet(false)}
              disabled={busy}
            />
            Only the stores I pick
          </label>

          {!wholeFleet && (
            <div className="mt-2 flex min-h-0 flex-col">
              {isLoading && (
                <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading stores...
                </p>
              )}
              {error && (
                <p className="text-xs text-amp-rust">
                  Could not load the store list: {error.message}
                </p>
              )}
              {!isLoading && !error && (
                <>
                  <div className="mb-1 flex items-center gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => setPicked(new Set(stores.map((st) => st.store_no)))}
                      disabled={busy}
                      className="text-amp-ink-muted underline hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setPicked(new Set())}
                      disabled={busy}
                      className="text-amp-ink-muted underline hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
                    >
                      Clear
                    </button>
                    <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      {picked.size} of {stores.length} selected
                    </span>
                  </div>
                  <div className="max-h-52 overflow-y-auto rounded border border-amp-line p-2 dark:border-amp-dark-line">
                    {stores.map((st) => (
                      <label
                        key={st.store_no}
                        className="flex items-center gap-2 py-0.5 text-sm text-amp-ink dark:text-amp-dark-ink"
                      >
                        <input
                          type="checkbox"
                          checked={picked.has(st.store_no)}
                          onChange={() => toggle(st.store_no)}
                          disabled={busy}
                        />
                        <span className="tabular-nums">{st.store_no}</span>
                        <span className="truncate text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {st.store_name ?? ''}
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </fieldset>

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Will run {scope.label}.
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(scope.store_nos)}
              disabled={busy || !scope.canRun}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
              {busy ? 'Running...' : 'Run allocation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary table pieces
// ---------------------------------------------------------------------------

function Th({ label, align = 'left' }: { label: string; align?: 'left' | 'right' }) {
  const help = COLUMN_HELP[label];
  return (
    <th className={`${align === 'right' ? 'text-right' : 'text-left'} font-medium px-2 py-2`}>
      <span className={`inline-flex items-baseline ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {help && <InfoTooltip label={label}>{help}</InfoTooltip>}
      </span>
    </th>
  );
}

function totalsOf(rows: AllocationSummaryRow[]) {
  return rows.reduce(
    (t, r) => ({
      active: t.active + r.active_sku_count,
      skus: t.skus + r.sku_count,
      lw: t.lw + r.sales_lw,
      suggested: t.suggested + r.suggested,
      final: t.final + r.final_alloc,
      ext: t.ext + r.ext_cost,
      stockouts: t.stockouts + r.stockouts,
    }),
    { active: 0, skus: 0, lw: 0, suggested: 0, final: 0, ext: 0, stockouts: 0 },
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function RetailAllocationInner() {
  const { user } = useSession();
  const perms = user?.groups ?? [];
  const canRead = hasAllocationRead(perms, user?.superAdmin);
  const canRun = hasAllocationRun(perms, user?.superAdmin);

  const { runs, isLoading: runsLoading } = useAllocationRuns();
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  useEffect(() => {
    const first = runs[0];
    if (first !== undefined && selectedRunId == null) {
      setSelectedRunId(first.id);
    }
  }, [runs, selectedRunId]);

  const { run, rows, isLoading: summaryLoading, error: summaryError } = useAllocationRunSummary(selectedRunId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const runAllocation = useRunAllocation();

  function handleConfirmRun(storeNos: string[]) {
    setToast(null);
    runAllocation.mutate(storeNos.length > 0 ? { store_nos: storeNos } : undefined, {
      onSuccess: (res) => {
        setConfirmOpen(false);
        setSelectedRunId(res.run.id);
        setToast({
          kind: 'ok',
          text: `Run #${res.run.id} complete, ${res.run.store_count ?? 0} ${
            res.run.store_count === 1 ? 'store' : 'stores'
          }.`,
        });
      },
      onError: (err) => {
        setToast({ kind: 'err', text: `Run failed: ${err.message}` });
      },
    });
  }

  async function handleExport() {
    if (selectedRunId == null) return;
    setExporting(true);
    setToast(null);
    try {
      await downloadAllocationXlsx(selectedRunId);
    } catch (e) {
      setToast({ kind: 'err', text: e instanceof Error ? e.message : 'Export failed' });
    } finally {
      setExporting(false);
    }
  }

  if (!user && !runsLoading) return null; // AuthGate handles unauthenticated state

  if (user && !canRead) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Allocation</h1>
        <AccessDeniedCard />
      </div>
    );
  }

  const isLoading = runsLoading || (selectedRunId != null && summaryLoading);
  const hasRuns = runs.length > 0;
  const totals = totalsOf(rows);

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="flex flex-wrap items-end justify-between mb-6 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Store
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Allocation</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Weekly warehouse-to-store allocation, summarized by category. The Excel export carries the
            full store-by-item detail.
          </p>
        </div>
        {/* w-full + wrap on mobile: `flex-shrink-0` alone stops this row from
            ever shrinking below its content's natural width (select + Export +
            Run allocation + pin, unwrapped, run ~500px), so on a phone it pushed
            the app shell's own scroll container (`[data-cockpit-main]`, the page
            body below md per COCKPIT.md 2.7) into horizontal scroll instead of
            wrapping onto a second line. Reverts to the original fixed-width,
            no-shrink behavior at md (768px) and up. */}
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-shrink-0">
          {hasRuns && (
            <select
              value={selectedRunId ?? ''}
              onChange={(e) => setSelectedRunId(Number(e.target.value))}
              aria-label="Select allocation run"
              className="w-full border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink md:w-auto"
            >
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  Run #{r.id} &middot; {r.week_ending ?? '--'} &middot; {formatDateTime(r.ran_at)}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting || selectedRunId == null}
            title="Download this run's full store-by-item allocation as Excel"
            className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export
          </button>
          {canRun && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={runAllocation.isPending}
              title="Run a fresh allocation"
              className="inline-flex items-center gap-1 rounded border border-amp-ink bg-amp-ink px-3 py-1.5 text-xs text-amp-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {runAllocation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
              Run allocation
            </button>
          )}
          <ToolPinToggle toolSlug="allocation" toolName="Allocation" />
        </div>
      </div>

      {toast && (
        <div
          className={`shrink-0 mb-4 flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
          <button type="button" onClick={() => setToast(null)} className="ml-4 text-xs">
            &times;
          </button>
        </div>
      )}

      {!hasRuns && !runsLoading ? (
        <NoRunsCard canRun={canRun} onRun={() => setConfirmOpen(true)} busy={runAllocation.isPending} />
      ) : (
        <>
          {run && (
            <div className="shrink-0 mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <StatBox value={formatUnits(run.total_units)} label="total units" />
              <StatBox value={formatAllocCost(run.total_cost)} label="total cost" />
              <StatBox value={String(run.stockout_count ?? 0)} label="stockouts" />
              <StatBox value={String(run.constrained_item_count ?? 0)} label="constrained items" />
              <StatBox value={String(run.store_count ?? 0)} label="stores" />
              <StatBox value={String(run.item_count ?? 0)} label="SKUs" />
            </div>
          )}

          <AllocationInsights runId={selectedRunId} disabled={selectedRunId == null || isLoading} />

          <DataGridShell total={rows.length} loaded={rows.length} countNoun="categories">
            <table className="min-w-full text-sm">
              <thead className={gridTheadClassName}>
                <tr>
                  <Th label="Category" />
                  <Th label="Sub-category" />
                  <Th label="SKUs" align="right" />
                  <Th label="Last wk" align="right" />
                  <Th label="Avg" align="right" />
                  <Th label="Trend" />
                  <Th label="Suggested" align="right" />
                  <Th label="Final" align="right" />
                  <Th label="Ext cost" align="right" />
                  <Th label="Stockouts" align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      Loading...
                    </td>
                  </tr>
                ) : summaryError ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-red-700 dark:text-red-300">
                      Could not load this run: {summaryError.message}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      This run has no lines.
                    </td>
                  </tr>
                ) : (
                  <>
                    {rows.map((r) => (
                      <tr
                        key={`${r.category}::${r.subcategory}`}
                        className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
                      >
                        <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">{r.category}</td>
                        <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {r.subcategory || '--'}
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                          {r.active_sku_count}
                          {r.sku_count > r.active_sku_count && (
                            <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                              {' '}/ {r.sku_count - r.active_sku_count} idle
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                          {formatUnits(r.sales_lw)}
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {formatUnits(r.sales_avg)}
                        </td>
                        <td className="px-2 py-2">
                          <TrendPill trend={r.trend} />
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {formatUnits(r.suggested)}
                        </td>
                        <td className="px-2 py-2 text-right font-medium text-amp-ink dark:text-amp-dark-ink">
                          {formatUnits(r.final_alloc)}
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                          {formatAllocCost(r.ext_cost)}
                        </td>
                        <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                          {r.stockouts > 0 ? r.stockouts : '--'}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-amp-line font-medium dark:border-amp-dark-line">
                      <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">All categories</td>
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{totals.active}</td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {formatUnits(totals.lw)}
                      </td>
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {formatUnits(totals.suggested)}
                      </td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {formatUnits(totals.final)}
                      </td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {formatAllocCost(totals.ext)}
                      </td>
                      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                        {totals.stockouts}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </DataGridShell>
        </>
      )}

      {confirmOpen && (
        <ConfirmRunModal
          onConfirm={handleConfirmRun}
          onClose={() => setConfirmOpen(false)}
          busy={runAllocation.isPending}
        />
      )}
    </div>
  );
}


export function RetailAllocation() {
  return (
    <ErrorBoundary>
      <RetailAllocationInner />
    </ErrorBoundary>
  );
}
